import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Main } from '@/components/layout/main';
import { PageHeader } from '@/components/layout/page-header';
import { DataTable } from '@/components/table';
import { DataTableColumnHeader } from '@/components/data-table-column-header';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { superAdminService } from '@/api/super-admin';
import { LawyerProfile, LawyerProfileCreate } from '@/types/super-admin';
import { handleServerError } from '@/utils/handle-server-error';
import { toast } from '@/hooks/use-toast';
import { ColumnDef } from '@tanstack/react-table';
import {
  Users,
  MoreHorizontal,
  Trash2,
  Send,
  Plus,
  Copy,
  Check,
  Mail,
  MailX,
  Eye,
  Download,
  Loader2,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

const LANDING_BASE = import.meta.env.VITE_LANDING_BASE_URL || 'https://avocatpro.ma';

const csvCell = (value: unknown) => {
  let text = value === null || value === undefined ? '' : String(value);
  // Neutralise spreadsheet formula injection from user-submitted profile data.
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

type FilterUnclaimed = 'all' | 'true' | 'false';
type FilterVerified = 'all' | 'true' | 'false';
type FilterPublished = 'all' | 'true' | 'false';

export default function LawyerProfilesManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openProfile = (id: string) =>
    navigate({ to: '/super-admin-lawyer-profiles/$id', params: { id } });

  const [profiles, setProfiles] = useState<LawyerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagesCnt, setPagesCnt] = useState(1);
  const [params, setParams] = useState<{ page: number; page_size: number; ordering?: string }>({
    page: 0,
    page_size: 10,
  });

  // Filters
  const [filterUnclaimed, setFilterUnclaimed] = useState<FilterUnclaimed>('all');
  const [filterVerified, setFilterVerified] = useState<FilterVerified>('all');
  const [filterPublished, setFilterPublished] = useState<FilterPublished>('all');
  const [filterName, setFilterName] = useState('');

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showClaimDialog, setShowClaimDialog] = useState(false);

  const [actionProfile, setActionProfile] = useState<LawyerProfile | null>(null);
  const [claimResult, setClaimResult] = useState<{ claim_url: string; email_sent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [newProfile, setNewProfile] = useState<LawyerProfileCreate>({
    full_name: '',
    full_name_fr: '',
    title: 'Avocat',
    bar_city: '',
    phone: '',
    email_public: '',
    cabinet_city: '',
    cabinet_country: 'Maroc',
    is_published: true,
  });

  useEffect(() => {
    fetchProfiles();
  }, [params, filterUnclaimed, filterVerified, filterPublished, filterName]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const apiParams: Record<string, unknown> = {
        page_size: params.page_size,
        page: params.page * params.page_size,
      };
      if (filterUnclaimed !== 'all') apiParams.unclaimed = filterUnclaimed === 'true';
      if (filterVerified !== 'all') apiParams.is_verified = filterVerified === 'true';
      if (filterPublished !== 'all') apiParams.is_published = filterPublished === 'true';
      if (filterName.trim()) apiParams.full_name = filterName.trim();

      const data = await superAdminService.getLawyerProfiles(apiParams as any);
      setProfiles(data.results);
      setPagesCnt(Math.ceil((data.count || data.results.length) / params.page_size));
    } catch (error) {
      handleServerError(error);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await superAdminService.createLawyerProfile(newProfile);
      setShowCreateDialog(false);
      setNewProfile({
        full_name: '',
        full_name_fr: '',
        title: 'Avocat',
        bar_city: '',
        phone: '',
        email_public: '',
        cabinet_city: '',
        cabinet_country: 'Maroc',
        is_published: true,
      });
      fetchProfiles();
      toast({ title: t('lp_created') });
    } catch (error) {
      handleServerError(error);
    }
  };

  const handleDelete = async () => {
    if (!actionProfile) return;
    try {
      await superAdminService.deleteLawyerProfile(
        actionProfile.id,
        actionProfile.cabinet_id !== null,
      );
      setShowDeleteDialog(false);
      setActionProfile(null);
      fetchProfiles();
      toast({ title: t('lp_deleted') });
    } catch (error) {
      handleServerError(error);
    }
  };

  const handleSendClaim = async (profile: LawyerProfile) => {
    try {
      const result = await superAdminService.sendClaimLink(profile.id);
      setClaimResult(result);
      setActionProfile(profile);
      setShowClaimDialog(true);
    } catch (error) {
      handleServerError(error);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const base: Record<string, unknown> = {};
      if (filterUnclaimed !== 'all') base.unclaimed = filterUnclaimed === 'true';
      if (filterVerified !== 'all') base.is_verified = filterVerified === 'true';
      if (filterPublished !== 'all') base.is_published = filterPublished === 'true';
      if (filterName.trim()) base.full_name = filterName.trim();

      // Walk every page so the file holds all profiles matching the filters.
      const all: LawyerProfile[] = [];
      const pageSize = 100;
      let offset = 0;
      for (;;) {
        const data = await superAdminService.getLawyerProfiles({ ...base, page_size: pageSize, page: offset, include_review_url: 1 } as any);
        all.push(...data.results);
        offset += pageSize;
        if (!data.next || data.results.length === 0 || (data.count && all.length >= data.count)) break;
      }

      const header = [
        'Nom complet', 'Nom complet (FR)', 'Titre', 'Barreau', 'Cabinet', 'Ville cabinet', 'Pays',
        'Téléphone', 'Email', 'Publié', 'Vérifié', 'Statut', 'Source', 'Créé le', 'Lien profil public', 'Lien de gestion (token)',
      ];
      const rows = all.map((p) => [
        p.full_name,
        p.full_name_fr,
        p.title,
        p.bar_city,
        p.cabinet_name,
        p.cabinet_city,
        p.cabinet_country,
        p.phone,
        p.email_public,
        p.is_published ? 'oui' : 'non',
        p.is_verified ? 'oui' : 'non',
        p.approval_status,
        p.submission_source,
        p.created_at ? new Date(p.created_at * 1000).toISOString().slice(0, 10) : '',
        p.slug ? `${LANDING_BASE}/fr/avocats/${p.slug}/` : '',
        p.review_url ?? '',
      ]);
      const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
      // BOM so Excel reads UTF-8 (Arabic / accents) correctly.
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `avocats-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast({ title: t('lp_export_done', { count: all.length }) });
    } catch (error) {
      handleServerError(error);
    } finally {
      setExporting(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const columns: ColumnDef<LawyerProfile>[] = useMemo(() => [
    {
      id: 'full_name',
      accessorKey: 'full_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lp_full_name')} />,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{p.full_name}</span>
            {p.full_name_fr && p.full_name_fr !== p.full_name && (
              <span className="text-xs text-muted-foreground">{p.full_name_fr}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'bar_city',
      accessorKey: 'bar_city',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('bar_city')} />,
      cell: ({ row }) => <span className="capitalize">{row.original.bar_city}</span>,
    },
    {
      id: 'email_public',
      accessorKey: 'email_public',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('email')} />,
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.email_public || '—'}</span>
      ),
    },
    {
      id: 'cabinet',
      accessorKey: 'cabinet_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lp_cabinet')} />,
      cell: ({ row }) => {
        const p = row.original;
        return p.cabinet_name ? (
          <span className="text-sm">{p.cabinet_name}</span>
        ) : (
          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
            {t('lp_unclaimed')}
          </Badge>
        );
      },
    },
    {
      id: 'is_verified',
      accessorKey: 'is_verified',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lp_verified')} />,
      cell: ({ row }) => (
        <Badge variant={row.original.is_verified ? 'default' : 'secondary'}>
          {row.original.is_verified ? t('lp_yes') : t('lp_no')}
        </Badge>
      ),
    },
    {
      id: 'is_published',
      accessorKey: 'is_published',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lp_published')} />,
      cell: ({ row }) => (
        <Badge
          variant={row.original.is_published ? 'default' : 'destructive'}
          className={row.original.is_published ? 'bg-green-600 hover:bg-green-600' : ''}
        >
          {row.original.is_published ? t('lp_published_yes') : t('lp_published_no')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-center">{t('actions')}</div>,
      cell: ({ row }) => {
        const profile = row.original;
        const isClaimed = profile.cabinet_id !== null;
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openProfile(profile.id)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('lp_view_details')}
                </DropdownMenuItem>
                {!isClaimed && (
                  <DropdownMenuItem onClick={() => handleSendClaim(profile)}>
                    <Send className="mr-2 h-4 w-4 text-blue-600" />
                    <span className="text-blue-600">{t('lp_send_claim')}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => { setActionProfile(profile); setShowDeleteDialog(true); }}
                  className="text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [t, navigate]);

  return (
    <div className="flex h-screen">
      <div className="flex-1">
        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Users className="h-5 w-5" />
                </span>
              }
              title={t('lp_page_title')}
              description={t('lp_page_desc')}
              actions={
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleExport} disabled={exporting}>
                    {exporting ? (
                      <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="me-2 h-4 w-4" />
                    )}
                    {t('lp_export_csv')}
                  </Button>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="me-2 h-4 w-4" />
                    {t('lp_add_profile')}
                  </Button>
                </div>
              }
            />

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Input
                placeholder={t('lp_search_name')}
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                className="w-56"
              />
              <Select value={filterUnclaimed} onValueChange={(v) => setFilterUnclaimed(v as FilterUnclaimed)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={t('lp_filter_claim_status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('lp_all')}</SelectItem>
                  <SelectItem value="true">{t('lp_unclaimed')}</SelectItem>
                  <SelectItem value="false">{t('lp_claimed')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterVerified} onValueChange={(v) => setFilterVerified(v as FilterVerified)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={t('lp_filter_verified')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('lp_all')}</SelectItem>
                  <SelectItem value="true">{t('lp_verified')}</SelectItem>
                  <SelectItem value="false">{t('lp_unverified')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterPublished} onValueChange={(v) => setFilterPublished(v as FilterPublished)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={t('lp_filter_published')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('lp_all')}</SelectItem>
                  <SelectItem value="true">{t('lp_published_yes')}</SelectItem>
                  <SelectItem value="false">{t('lp_published_no')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DataTable
              columns={columns}
              data={profiles}
              pages={pagesCnt}
              params={params}
              setParams={setParams}
              showPagination={true}
              onRowClick={(row: LawyerProfile) => openProfile(row.id)}
              storageKey="super-admin-lawyer-profiles-table"
            />

            {!loading && profiles.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  {t('lp_no_profiles')}
                </h3>
              </div>
            )}
          </div>

          {/* Create Dialog */}
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('lp_add_profile')}</DialogTitle>
                <DialogDescription>{t('lp_add_profile_desc')}</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">{t('lp_full_name')} *</Label>
                  <Input
                    value={newProfile.full_name}
                    onChange={(e) => setNewProfile({ ...newProfile, full_name: e.target.value })}
                    className="col-span-3"
                    placeholder="Maître X"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">{t('lp_full_name_fr')}</Label>
                  <Input
                    value={newProfile.full_name_fr}
                    onChange={(e) => setNewProfile({ ...newProfile, full_name_fr: e.target.value })}
                    className="col-span-3"
                    placeholder="X"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">{t('bar_city')}</Label>
                  <Input
                    value={newProfile.bar_city}
                    onChange={(e) => setNewProfile({ ...newProfile, bar_city: e.target.value })}
                    className="col-span-3"
                    placeholder="casablanca"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">{t('phone')}</Label>
                  <Input
                    value={newProfile.phone}
                    onChange={(e) => setNewProfile({ ...newProfile, phone: e.target.value })}
                    className="col-span-3"
                    placeholder="+212 ..."
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">{t('email')}</Label>
                  <Input
                    value={newProfile.email_public}
                    onChange={(e) => setNewProfile({ ...newProfile, email_public: e.target.value })}
                    className="col-span-3"
                    placeholder="avocat@example.com"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">{t('lp_city')}</Label>
                  <Input
                    value={newProfile.cabinet_city}
                    onChange={(e) => setNewProfile({ ...newProfile, cabinet_city: e.target.value })}
                    className="col-span-3"
                    placeholder="Casablanca"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleCreate} disabled={!newProfile.full_name.trim()}>
                  {t('create')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('lp_delete_title')}</DialogTitle>
                <DialogDescription>
                  {t('delete_cabinet_warning')} <strong>{actionProfile?.full_name}</strong>. {t('action_cannot_be_undone')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  {t('delete')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Claim Link Dialog */}
          <Dialog open={showClaimDialog} onOpenChange={(open) => { setShowClaimDialog(open); if (!open) setClaimResult(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('lp_claim_link_title')}</DialogTitle>
                <DialogDescription>{actionProfile?.full_name}</DialogDescription>
              </DialogHeader>
              {claimResult && (
                <div className="space-y-4 py-2">
                  {claimResult.email_sent ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <Mail className="h-4 w-4 shrink-0" />
                      {t('lp_email_sent')}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <MailX className="h-4 w-4 shrink-0" />
                      {t('lp_email_not_sent')}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>{t('lp_claim_url')}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={claimResult.claim_url}
                        className="font-mono text-xs"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(claimResult.claim_url)}
                        className="shrink-0"
                      >
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setShowClaimDialog(false)}>
                  {t('close')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Main>
      </div>
    </div>
  );
}
